import type {
	PickupRequest,
	PickupRequestDraft,
	PickupRequestStatus,
	PickupType,
	PickupWindow,
} from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import { type ReactNode, useRef } from "react";
import { Button } from "../primitives/Button";
import { Combobox, type ComboboxOption } from "../primitives/Combobox";
import { Drawer } from "../primitives/Drawer";
import { Input, NumberInput } from "../primitives/Input";
import { Select } from "../primitives/Select";
import type { Tone } from "../styles";
import { DatePicker } from "./form/DatePicker";
import { Form, FormErrorSummary, FormField, FormSection } from "./form/Form";
import { useApiForm } from "./form/useApiForm";
import { StatusChip } from "./StatusChip";

/**
 * A pickup request's form and its status pill (EP03-S21, DA-03-08), shared by
 * the Admin Console's Delivery → Pickups and the Store Portal's Orders →
 * Pickup requests. Each app loads its own data (the console searches every
 * store and city; the portal only its own store and the cities it may pick)
 * and hands it here, as the Messages panel does.
 *
 * The city is picked and the province comes with it, as on the portal's New
 * Order form. The map pin is two optional coordinates: a map picker arrives
 * with route optimisation's map (EP99-S03), and a pin pasted from Maps is what
 * a dispatcher has today.
 */

export const PICKUP_TYPES: readonly PickupType[] = [
	"inbound_stock",
	"direct_to_customer",
	"return_from_customer",
];

export const PICKUP_WINDOWS: readonly PickupWindow[] = ["morning", "afternoon", "evening"];

export const PICKUP_STATUSES: readonly PickupRequestStatus[] = [
	"requested",
	"scheduled",
	"driver_assigned",
	"collected",
	"received",
	"cancelled",
	"failed",
];

const TONES: Record<PickupRequestStatus, Tone> = {
	requested: "info",
	scheduled: "info",
	driver_assigned: "brand",
	collected: "success",
	received: "success",
	cancelled: "neutral",
	failed: "warning",
};

/** A pickup's status as a pill. */
export function PickupStatusChip({ status, t }: { status: PickupRequestStatus; t: Translate }) {
	return (
		<StatusChip tone={TONES[status]} size="sm">
			{t(`delivery.pickups.status.${status}`)}
		</StatusChip>
	);
}

/** A city the drawer offers, carrying its province. */
export interface PickupPlace {
	cityId: string;
	cityName: string;
	provinceId: string;
	provinceName: string;
}

export interface PickupFormValues {
	storeId: string | null;
	storeLabel: string;
	pickupType: PickupType;
	purchaseOrderId: string;
	returnId: string;
	cityId: string | null;
	cityName: string;
	provinceId: string;
	provinceName: string;
	address: string;
	lat: string;
	lng: string;
	contactName: string;
	contactPhone: string;
	requestedDate: string | null;
	window: PickupWindow;
	expectedCount: string;
	notes: string;
}

/**
 * The form's values for a new request, or for editing one. A new request may
 * start from a known place — the portal's last pickup (EP10-S14).
 */
export function pickupFormValues(
	pickup?: PickupRequest,
	startFrom?: PickupRequest["origin"],
): PickupFormValues {
	const o = pickup?.origin ?? startFrom;
	return {
		storeId: pickup?.storeId ?? null,
		storeLabel: pickup?.storeName ?? pickup?.storeId ?? "",
		pickupType: pickup?.pickupType ?? "inbound_stock",
		purchaseOrderId: pickup?.purchaseOrderId ?? "",
		returnId: pickup?.returnId ?? "",
		cityId: o?.cityId ?? null,
		cityName: o?.cityName ?? "",
		provinceId: o?.provinceId ?? "",
		provinceName: o?.provinceName ?? "",
		address: o?.address ?? "",
		lat: o?.geo ? String(o.geo.lat) : "",
		lng: o?.geo ? String(o.geo.lng) : "",
		contactName: o?.contactName ?? "",
		contactPhone: o?.contactPhone ?? "",
		requestedDate: pickup?.requestedDate ?? null,
		window: pickup?.window ?? "morning",
		expectedCount: pickup ? String(pickup.expectedCount) : "1",
		notes: pickup?.notes ?? "",
	};
}

/** A coordinate typed as text; null when it is not a number in range. */
function coordinate(raw: string, limit: number): number | null {
	const n = Number(raw.trim().replace(",", "."));
	return raw.trim() !== "" && Number.isFinite(n) && Math.abs(n) <= limit ? n : null;
}

/**
 * The client-side checks: what is missing or plainly wrong, keyed by the
 * server's own field names so its refusals land on the same inputs.
 */
export function validatePickup(
	v: PickupFormValues,
	today: string,
	needsStore: boolean,
	t: Translate,
): Record<string, string> {
	const errors: Record<string, string> = {};
	const required = t("delivery.pickups.form.required");
	if (needsStore && v.storeId === null) {
		errors.storeId = required;
	}
	if (v.pickupType === "return_from_customer" && v.returnId.trim() === "") {
		errors.returnId = t("delivery.pickups.form.returnRequired");
	}
	if (v.cityId === null) {
		errors["origin.cityId"] = required;
	}
	for (const [key, field] of [
		["address", "origin.address"],
		["contactName", "origin.contactName"],
		["contactPhone", "origin.contactPhone"],
	] as const) {
		if (v[key].trim() === "") {
			errors[field] = required;
		}
	}
	if (v.requestedDate === null) {
		errors.requestedDate = required;
	} else if (v.requestedDate < today) {
		errors.requestedDate = t("delivery.pickups.form.pastDate");
	}
	if (!/^[1-9][0-9]*$/.test(v.expectedCount.trim())) {
		errors.expectedCount = t("delivery.pickups.form.countInvalid");
	}
	const pinned = v.lat.trim() !== "" || v.lng.trim() !== "";
	if (pinned && (coordinate(v.lat, 90) === null || coordinate(v.lng, 180) === null)) {
		errors["origin.geo"] = t("delivery.pickups.form.pinInvalid");
	}
	return errors;
}

/** The request body the values make. */
export function pickupDraftOf(v: PickupFormValues): PickupRequestDraft {
	const lat = coordinate(v.lat, 90);
	const lng = coordinate(v.lng, 180);
	return {
		...(v.storeId === null ? {} : { storeId: v.storeId }),
		pickupType: v.pickupType,
		...(v.pickupType === "inbound_stock" && v.purchaseOrderId.trim() !== ""
			? { purchaseOrderId: v.purchaseOrderId.trim() }
			: {}),
		...(v.pickupType === "return_from_customer" ? { returnId: v.returnId.trim() } : {}),
		origin: {
			address: v.address.trim(),
			provinceId: v.provinceId,
			...(v.cityId === null ? {} : { cityId: v.cityId }),
			...(lat === null || lng === null ? {} : { geo: { lat, lng } }),
			contactName: v.contactName.trim(),
			contactPhone: v.contactPhone.trim(),
		},
		requestedDate: v.requestedDate ?? "",
		window: v.window,
		expectedCount: Number(v.expectedCount.trim()),
		...(v.notes.trim() === "" ? {} : { notes: v.notes.trim() }),
	};
}

export interface PickupDrawerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	t: Translate;
	/** The tenant's day, `YYYY-MM-DD`: the earliest a pickup may be asked for. */
	today: string;
	/** The request being edited (only while `requested`); absent creates one. */
	pickup?: PickupRequest | undefined;
	/** The console's store picker; the portal's store is its own and has none. */
	searchStores?: ((query: string, signal: AbortSignal) => Promise<ComboboxOption[]>) | undefined;
	searchCities: (query: string, signal: AbortSignal) => Promise<PickupPlace[]>;
	/** Saves the draft: creates, or edits `pickup`. */
	onSubmit: (draft: PickupRequestDraft) => Promise<void>;
	/** Where a new request starts: the portal's last pickup's place (EP10-S14). */
	startFrom?: PickupRequest["origin"] | undefined;
	/** What the pickup costs, drawn under the day (EP10-S14); it is handed the day. */
	feePreview?: ((date: string | null) => ReactNode) | undefined;
}

export function PickupDrawer({
	open,
	onOpenChange,
	t,
	today,
	pickup,
	searchStores,
	searchCities,
	onSubmit,
	startFrom,
	feePreview,
}: PickupDrawerProps) {
	// The last search's cities, so a choice can fill its province.
	const places = useRef(new Map<string, PickupPlace>()).current;
	const form = useApiForm<PickupFormValues>({
		initial: pickupFormValues(pickup, startFrom),
		validate: (values) =>
			validatePickup(values, today, searchStores !== undefined && pickup === undefined, t),
		submit: async (values) => {
			await onSubmit(pickupDraftOf(values));
			onOpenChange(false);
		},
	});
	const v = form.values;
	const text = (key: keyof PickupFormValues, name: string, label: string, optional = false) => (
		<FormField name={name} label={label} required={!optional} optional={optional}>
			{(control) => (
				<Input
					{...control}
					value={String(v[key] ?? "")}
					onChange={(e) => form.set(key, e.target.value)}
				/>
			)}
		</FormField>
	);

	return (
		<Drawer
			open={open}
			onOpenChange={onOpenChange}
			size="md"
			title={t(pickup ? "delivery.pickups.form.editTitle" : "delivery.pickups.form.newTitle")}
			description={t("delivery.pickups.form.subtitle")}
			dirty={form.dirty}
			closeLabel={t("ui.actions.close")}
			backLabel={t("ui.actions.back")}
			footer={
				<>
					<Button variant="secondary" onClick={() => onOpenChange(false)}>
						{t("ui.actions.cancel")}
					</Button>
					<Button variant="primary" loading={form.submitting} onClick={() => form.handleSubmit()}>
						{t(pickup ? "ui.actions.save" : "delivery.pickups.actions.request")}
					</Button>
				</>
			}
		>
			<Form form={form}>
				<FormErrorSummary form={form} t={t} />
				<FormSection title={t("delivery.pickups.form.what")} columns={2}>
					{searchStores !== undefined && pickup === undefined && (
						<FormField name="storeId" label={t("delivery.pickups.columns.store")} required>
							{(control) => (
								<Combobox
									{...control}
									value={v.storeId}
									selectedLabel={v.storeLabel}
									placeholder={t("delivery.pickups.form.storePlaceholder")}
									emptyLabel={t("delivery.pickups.form.noMatch")}
									clearLabel={t("ui.actions.clear")}
									loadOptions={searchStores}
									onValueChange={(value) => form.set("storeId", value)}
								/>
							)}
						</FormField>
					)}
					<FormField name="pickupType" label={t("delivery.pickups.columns.type")} required>
						{(control) => (
							<Select
								{...control}
								value={v.pickupType}
								onValueChange={(value) => form.set("pickupType", value as PickupType)}
								options={PICKUP_TYPES.map((type) => ({
									value: type,
									label: t(`delivery.pickups.type.${type}`),
								}))}
							/>
						)}
					</FormField>
					{v.pickupType === "inbound_stock" &&
						text(
							"purchaseOrderId",
							"purchaseOrderId",
							t("delivery.pickups.form.purchaseOrder"),
							true,
						)}
					{v.pickupType === "return_from_customer" &&
						text("returnId", "returnId", t("delivery.pickups.form.return"))}
					<FormField name="expectedCount" label={t("delivery.pickups.columns.expected")} required>
						{(control) => (
							<NumberInput
								{...control}
								integerOnly
								value={v.expectedCount}
								onChange={(e) => form.set("expectedCount", e.target.value)}
							/>
						)}
					</FormField>
				</FormSection>
				<FormSection title={t("delivery.pickups.form.where")} columns={2}>
					<FormField name="origin.cityId" label={t("delivery.pickups.form.city")} required>
						{(control) => (
							<Combobox
								{...control}
								value={v.cityId}
								selectedLabel={v.cityName}
								placeholder={t("delivery.pickups.form.cityPlaceholder")}
								emptyLabel={t("delivery.pickups.form.noMatch")}
								loadOptions={async (query, signal) => {
									const found = await searchCities(query, signal);
									for (const place of found) {
										places.set(place.cityId, place);
									}
									return found.map((place) => ({
										value: place.cityId,
										label: place.cityName,
										hint: place.provinceName,
									}));
								}}
								onValueChange={(cityId) => {
									const place = cityId === null ? undefined : places.get(cityId);
									form.set("cityId", place?.cityId ?? null);
									form.set("cityName", place?.cityName ?? "");
									form.set("provinceId", place?.provinceId ?? "");
									form.set("provinceName", place?.provinceName ?? "");
								}}
							/>
						)}
					</FormField>
					<FormField name="origin.provinceId" label={t("delivery.pickups.form.province")}>
						{(control) => <Input {...control} value={v.provinceName} readOnly />}
					</FormField>
					{text("address", "origin.address", t("delivery.pickups.form.address"))}
					<FormField
						name="origin.geo"
						label={t("delivery.pickups.form.pin")}
						hint={t("delivery.pickups.form.pinHint")}
						optional
					>
						{(control) => (
							<div className="flex gap-2">
								<Input
									{...control}
									aria-label={t("delivery.pickups.form.lat")}
									inputMode="decimal"
									placeholder="33.89"
									value={v.lat}
									onChange={(e) => form.set("lat", e.target.value)}
								/>
								<Input
									aria-label={t("delivery.pickups.form.lng")}
									inputMode="decimal"
									placeholder="35.50"
									value={v.lng}
									onChange={(e) => form.set("lng", e.target.value)}
								/>
							</div>
						)}
					</FormField>
					{text("contactName", "origin.contactName", t("delivery.pickups.form.contactName"))}
					{text("contactPhone", "origin.contactPhone", t("delivery.pickups.form.contactPhone"))}
				</FormSection>
				<FormSection title={t("delivery.pickups.form.when")} columns={2}>
					<FormField name="requestedDate" label={t("delivery.pickups.columns.date")} required>
						{(control) => (
							<DatePicker
								{...control}
								min={today}
								value={v.requestedDate}
								onChange={(value) => form.set("requestedDate", value)}
							/>
						)}
					</FormField>
					<FormField name="window" label={t("delivery.pickups.form.window")} required>
						{(control) => (
							<Select
								{...control}
								value={v.window}
								onValueChange={(value) => form.set("window", value as PickupWindow)}
								options={PICKUP_WINDOWS.map((w) => ({
									value: w,
									label: t(`delivery.pickups.window.${w}`),
								}))}
							/>
						)}
					</FormField>
					{text("notes", "notes", t("delivery.pickups.form.notes"), true)}
					{feePreview?.(v.requestedDate)}
				</FormSection>
			</Form>
		</Drawer>
	);
}
